import { differenceInDays, intervalToDuration } from 'date-fns';

import { NumberFormat } from '@ecp/common/src/utils/utils';
import { PriceUtils } from '@ecp/common/src/utils/price/PriceUtils';
import {
  DIALOG_MESSAGE,
  GOOD_RECOMMENDATION_TAB,
  OD_ORDER_MAX_COUNT_STANDARD,
  PD_DELI_COST_FORM,
  PD_GOOD_TYPE,
} from '@ecp/common/src/const/good';
import { DEFAULT_PRICE_FORMAT } from '@ecp/common/src/const/price/priceConst';

const getRepresentativeImage = (imageList = []) => {
  if (!Array.isArray(imageList)) {
    return '';
  }
  return imageList.find((el) => el?.displaySequence === 1)?.src;
};

const hasArray = (values = []) => {
  return Array.isArray(values) && values.length;
};

export const differenceInDate = (endDate) => {
  if (!endDate) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const { hours, minutes, seconds } = intervalToDuration({ start: new Date(), end: endDate });
  const days = differenceInDays(endDate, new Date());

  return { days, hours, minutes, seconds };
};

export const getDateLabel = (itemName) => {
  const week = ['일', '월', '화', '수', '목', '금', '토'];
  const [date, ...rest_opt] = itemName.split('/');
  const [yyyy, mm, dd] = date.split('-');
  const dayOfWeek = week[new Date(date).getDay()];

  return [`${yyyy}년 ${mm}월 ${dd}일 (${dayOfWeek})`, ...rest_opt].join('/');
};

export const goodOptionValidation = ({
  checkOrderAvailableResult = [],
  selectedOptionList,
  ordPossibleMaxQtyStandardCode,
  ordPossibleMaxQty,
  ordPossibleMinQty,
  ordPossibleMaxAmount,
  ordPossibleMaxFormattedAmount,
  ordPossibleMinAmount,
  ordPossibleMinFormattedAmount,
  showBtnBulkp,
  isBulkpOrder,
  bulkpOrdPossibleMinQty = 1,
  priceFormat = DEFAULT_PRICE_FORMAT,
}) => {
  const priceUtils = new PriceUtils();
  const internalOrdPossibleMaxAmount = priceUtils.getDisplayPrice({
    priceFormat,
    price: ordPossibleMaxAmount,
    formattedPrice: ordPossibleMaxFormattedAmount,
  });
  const internalOrdPossibleMinAmount = priceUtils.getDisplayPrice({
    priceFormat,
    price: ordPossibleMinAmount,
    formattedPrice: ordPossibleMinFormattedAmount,
  });
  const orderNotAvailableList = checkOrderAvailableResult.filter(
    (res) =>
      !res.availableMaxOrderQuantity ||
      !res.availableOrder ||
      !res.availableSamsungFamilySale ||
      !res.availableStock ||
      !res.availableNoPriceProduct ||
      showBtnBulkp
  );

  if (orderNotAvailableList.length === 0) {
    return {
      message: '',
    };
  }
  const currentOrderQuantity = selectedOptionList.reduce((acc, elem) => {
    return acc + (elem.additionalItem ? 0 : elem.orderQuantity);
  }, 0);

  const { availableMaxOrderQuantity, availableOrder, availableSamsungFamilySale, availableStock, availableNoPriceProduct, orderGoodRequestList } =
    orderNotAvailableList[0];
  let message;
  let reload = false;
  let rollback = true;
  if (!availableSamsungFamilySale) {
    message = '회원당 이번달 구매 가능한 수량을 초과하였습니다.(동일품목 1개, 추가옵션 건수 제외)';
  } else if (isBulkpOrder) {
    //대량주문인 경우, 대량주문 최소수량 기준만 체크하고 이후 체크 로직은 안함
    if (bulkpOrdPossibleMinQty > currentOrderQuantity) {
      return { message: DIALOG_MESSAGE.NOT_ENOUGH_BULKP_ORDER_MIN_QTY };
    }
  } else if (!availableMaxOrderQuantity) {
    const currentOrderAmount = selectedOptionList.reduce((acc, elem) => {
      const internalDiscountPrice = priceUtils.getDisplayPrice({
        priceFormat,
        price: elem.discountPrice,
        formattedPrice: elem.discountFormattedPrice,
      });
      return acc + (elem.additionalItem ? 0 : internalDiscountPrice * elem.orderQuantity);
    }, 0);

    const { totalOrderQuantity } = orderGoodRequestList[0];

    if (showBtnBulkp && !isBulkpOrder) {
      //대량주문 버튼이 있는데 선물or바로주문으로 대량주문 최소수량을 넘어가면 대량주문으로 유도하도록 안내
      if (bulkpOrdPossibleMinQty <= currentOrderQuantity) {
        return { message: DIALOG_MESSAGE.OPTION_QTY_IS_ONLY_BULKP_ORDER, rollback: false, reAlert: false };
      } else if (ordPossibleMaxQty<currentOrderQuantity && currentOrderQuantity<bulkpOrdPossibleMinQty) {
        return { message: DIALOG_MESSAGE.BETWEEN_ORDER_MAX_QTY_AND_BULKP_ORDER_MIN_QTY.replace('{orderMaxQty}', ordPossibleMaxQty).replace('{bulkpOrderMinQty}', bulkpOrdPossibleMinQty), rollback: false, reAlert: false };
      }
    }

    const orderUnit = {
      [OD_ORDER_MAX_COUNT_STANDARD.ORDER]: '주문당',
      [OD_ORDER_MAX_COUNT_STANDARD.MEMBER]: '회원당',
      [OD_ORDER_MAX_COUNT_STANDARD.ONLY_ORDER_MEMBER]: '한 주문당 최대 1개',
      [OD_ORDER_MAX_COUNT_STANDARD.DAILY]: '일별',
    };
    switch (ordPossibleMaxQtyStandardCode) {
      case OD_ORDER_MAX_COUNT_STANDARD.ONLY_ORDER_MEMBER:
        if (ordPossibleMaxQty !== 0) {
          message = `해당 상품은 ${orderUnit[ordPossibleMaxQtyStandardCode]}, 회원당 최대 ${ordPossibleMaxQty}개 까지 주문이 가능합니다.(추가옵션 건수 제외)`;
        } else {
          message = `해당 상품은 ${orderUnit[ordPossibleMaxQtyStandardCode]} 까지 주문이 가능합니다.`;
        }
        break;
      case OD_ORDER_MAX_COUNT_STANDARD.ORDER:
      case OD_ORDER_MAX_COUNT_STANDARD.MEMBER:
      case OD_ORDER_MAX_COUNT_STANDARD.DAILY:
        // 주문단위는 요청한 수량으로 비교
        // 회원단위와 일별단위는 과거 구매 수량 + 요청 수량으로 비교
        const qty = ordPossibleMaxQtyStandardCode === OD_ORDER_MAX_COUNT_STANDARD.ORDER ? currentOrderQuantity : totalOrderQuantity;
        if (ordPossibleMinQty !== 0 && qty < ordPossibleMinQty) {
          message = `해당 상품은 ${orderUnit[ordPossibleMaxQtyStandardCode]} 최소 ${ordPossibleMinQty}개 이상 주문이 가능합니다.(추가옵션 건수 제외)`;
          rollback = false;
        } else {
          message = `해당 상품은 ${orderUnit[ordPossibleMaxQtyStandardCode]} 최대 ${ordPossibleMaxQty}개 까지 주문이 가능합니다.(추가옵션 건수 제외)`;
        }
        break;
      case OD_ORDER_MAX_COUNT_STANDARD.AMOUNT:
        if (internalOrdPossibleMinAmount !== 0 && currentOrderAmount < internalOrdPossibleMinAmount) {
          message = `해당 상품은 주문당 최소 ${internalOrdPossibleMinAmount}${priceFormat} 이상 주문이 가능합니다.(추가옵션 금액 제외)`;
          rollback = false;
        } else {
          message = `해당 상품은 주문당 최대 ${internalOrdPossibleMaxAmount}${priceFormat} 까지 주문이 가능합니다.(추가옵션 금액 제외)`;
        }
        break;
      default:
        break;
    }
  } else if (!availableOrder) {
    message = '죄송합니다. 해당 상품이 품절되었습니다.';
    reload = true;
  } else if (!availableStock) {
    message = '죄송합니다.\n주문할 수 있는 재고수량을 초과하였습니다.';
  }
  if (!availableNoPriceProduct) {
    message = '죄송합니다. 상품에 오류가 있습니다.';
  }

  return {
    message,
    reload,
    rollback,
  };
};

export const getDeliverySubInfo = ({
  deliveryCostFormCode,
  deliveryStandardAmount,
  deliveryStandardFormattedAmount,
  priceFormat = DEFAULT_PRICE_FORMAT,
}) => {
  const priceUtils = new PriceUtils();

  if (PD_DELI_COST_FORM.CONDITIONAL === deliveryCostFormCode) {
    const internalPrice = priceUtils.getDisplayPrice({
      priceFormat,
      price: NumberFormat(deliveryStandardAmount),
      formattedPrice: NumberFormat(deliveryStandardFormattedAmount),
    });

    return ` (${internalPrice}${priceFormat} 이상 무료배송)`;
  }

  if (PD_DELI_COST_FORM.PAID === deliveryCostFormCode) {
    return ' (유료배송)';
  }

  return '';
};

export const showOriginalPrice = (releasePrice, originalPrice, departmentStorePrice, discountPrice) => {
  if (releasePrice > 0 && originalPrice >= releasePrice) {
    return false;
  }

  if (departmentStorePrice > 0 && originalPrice >= departmentStorePrice) {
    return false;
  }

  return discountPrice < originalPrice;
};

export const recommendationTabList = ({ goodTypeCode, foodClass, isGroup, deliveryCostSave }) => {
  const showDelivery = !isGroup && deliveryCostSave;
  const deliveryTab = showDelivery ? [GOOD_RECOMMENDATION_TAB.DELIVERY] : [];

  if (
    [PD_GOOD_TYPE.GIFT_CARD, PD_GOOD_TYPE.TICKET, PD_GOOD_TYPE.PARK_POINT, PD_GOOD_TYPE.GIFTISHOW].includes(
      goodTypeCode
    )
  ) {
    return [
      GOOD_RECOMMENDATION_TAB.CATEGORY,
      GOOD_RECOMMENDATION_TAB.ORDER,
      GOOD_RECOMMENDATION_TAB.VIEW,
      ...deliveryTab,
    ];
  }

  if (PD_GOOD_TYPE.REGULAR_DELIVERY === goodTypeCode) {
    return [GOOD_RECOMMENDATION_TAB.ORDER, GOOD_RECOMMENDATION_TAB.CATEGORY, GOOD_RECOMMENDATION_TAB.VIEW];
  }

  if (PD_GOOD_TYPE.NORMAL === goodTypeCode && foodClass) {
    return [
      ...deliveryTab,
      GOOD_RECOMMENDATION_TAB.ORDER,
      GOOD_RECOMMENDATION_TAB.CATEGORY,
      GOOD_RECOMMENDATION_TAB.VIEW,
    ];
  }

  return [
    GOOD_RECOMMENDATION_TAB.ORDER,
    GOOD_RECOMMENDATION_TAB.CATEGORY,
    GOOD_RECOMMENDATION_TAB.VIEW,
    ...deliveryTab,
  ];
};

export const codeNameToDropdownList = (list) => {
  return list.map(({ code, name }) => ({ key: code, label: name }));
};

export const stringToDropdownList = (list) => {
  return list.map((it) => ({ key: it, label: it }));
};

export default {
  getRepresentativeImage,
  hasArray,
  differenceInDate,
  getDateLabel,
  goodOptionValidation,
  getDeliverySubInfo,
  showOriginalPrice,
  recommendationTabList,
  codeNameToDropdownList,
  stringToDropdownList,
};
